import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, removeAmPm } from "../../utility/formatDate";
import axiosInstance from "../../utility/axiosInstance";
import { formatTime } from "../../utility/formatDate";

const formatTimeTo24 = (timeStr) => {
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours, 10);

  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

// utils/slotPeriod.js (or at top of file)
export const getSlotPeriod = (slotStart) => {
  // slotStart expected in human form like "12:00:00 PM" or "12:00 PM"
  // formatTimeTo24 should convert to "HH:mm"
  const time24 = formatTimeTo24(slotStart); // "HH:mm"
  const [hourStr, minuteStr] = time24.split(":");
  const hour = parseInt(hourStr, 10);

  // Define ranges (adjust if needed)
  if (hour >= 5 && hour < 12) return "Morning";
  if (hour >= 12 && hour < 17) return "Afternoon";
  if (hour >= 17 && hour <= 21) return "Evening";
  return "Other"; // fallback (night / midnight etc.)
};

const periodIconMap = {
  Morning: "/icon-1.png",
  Afternoon: "/icon-2.png",
  Evening: "/icon-3.png",
  Other: "/icon-1.png",
};

const SlotList = ({
  dateSlots,
  doctorInfo,
  slotDate,
  selectedDoctor,
  selectedSpecialty,
  addNotification,
}) => {
  const [loadingSlot, setLoadingSlot] = useState(null);
  const navigate = useNavigate();

  const datesend = formatDate(slotDate);

  // Group slots into periods (useMemo to avoid re-calculation on every render)
  const periodWiseSlots = useMemo(() => {
    const grouped = {
      Morning: [],
      Afternoon: [],
      Evening: [],
      Other: [],
    };

    (dateSlots || []).forEach((slot) => {
      // slot.start expected to be the display start (e.g., "12:00 PM" or "12:00")
      const period = getSlotPeriod(slot.start);
      if (!grouped[period]) grouped[period] = [];
      grouped[period].push(slot);
    });

    return grouped;
  }, [dateSlots]);

  const checkSlotAvailability = async (slot) => {
    setLoadingSlot(slot.start);

    const start = removeAmPm(slot.start);
    const end = removeAmPm(slot.end);

    const isDev = process.env.REACT_APP_PHASE === "development";

    try {
      const response = await axiosInstance.post(
        isDev
          ? "/TApiQuery?webformid=wf3360&terminalid=1%2083.82.126.219"
          : `/TApiQuery?webformid=wf3358&terminalid=${process.env.REACT_APP_TERMINAL_ID}`,
        {
          data: JSON.stringify([
            {
              DOCTORID: selectedDoctor?.value,
              FROMDTS: datesend,
              FROMTM: start,
              TOTM: end,
            },
          ]),
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      if (response.data?.[0]?.SLOTDTLS === "true") {
        navigate("/appointment-detail", {
          state: {
            doctorInfo: doctorInfo,
            selectedDoctorId: selectedDoctor?.value,
            selectedDoctorImage: selectedDoctor?.image,
            selectedDoctorAbout: selectedDoctor?.about,
            selectedSpecialty: selectedSpecialty?.value,
            appDate: slotDate,
            startTime: slot.start,
            endTime: slot.end,
          },
        });
      } else {
        addNotification("This slot is already booked");
      }
    } catch (error) {
      alert("Error checking slot availability");
      console.error("Error checking slot:", error);
    } finally {
      setLoadingSlot(null);
    }
  };

  const renderPeriodBlock = (period, slots) => {
    if (!slots.length) return null;

    return (
      <div key={period} className="mb-6">
        {/* Period Header */}
        <div className="flex items-center gap-3 mb-3">
          <img src={periodIconMap[period]} className="w-7 h-7" />
          <strong className="text-lg text-gray-900">{period}</strong>
        </div>

        {/* MOBILE SCROLL (visible only on small screens) */}
        <div className="flex gap-3 overflow-x-auto sm:hidden pb-2">
          {slots.map((slot, idx) => (
            <div
              key={idx}
              onClick={() => checkSlotAvailability(slot)}
              className={`whitespace-nowrap px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer flex-shrink-0 transition
        ${
          loadingSlot === slot.start
            ? "bg-[#0095da] text-white border-[#0095da]"
            : "bg-white text-gray-700 border-gray-300 hover:border-[#0095da] hover:bg-[#0095da]/10"
        }`}
            >
              {formatTime(slot.start)} — {formatTime(slot.end)}
            </div>
          ))}
        </div>

        {/* DESKTOP GRID (hidden on mobile) */}
        <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {slots.map((slot, idx) => (
            <div
              key={idx}
              onClick={() => checkSlotAvailability(slot)}
              className={`cursor-pointer px-1.2 py-1.5 text-center rounded-lg border text-sm font-medium transition
        ${
          loadingSlot === slot.start
            ? "bg-[#0095da] text-white border-[#0095da]"
            : "bg-white text-gray-700 border-gray-300 hover:border-[#0095da] hover:bg-[#0095da]/10"
        }`}
            >
              {formatTime(slot.start)} — {formatTime(slot.end)}
              {loadingSlot === slot.start && (
                <div className="mt-1 h-1.5 bg-white rounded-full animate-pulse" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {renderPeriodBlock("Morning", periodWiseSlots.Morning)}
      {renderPeriodBlock("Afternoon", periodWiseSlots.Afternoon)}
      {renderPeriodBlock("Evening", periodWiseSlots.Evening)}
      {renderPeriodBlock("Other", periodWiseSlots.Other)}
    </div>
  );
};

export default SlotList;
